import { useState } from 'react';
import { Car, Plus } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import {
  useMileageEntries,
  useCreateMileage,
  useSubmitMileage,
  useApproveMileage,
  useRejectMileage,
  usePayrollSettings,
} from '@/hooks/use-payroll';
import { usePersons } from '@/hooks/use-persons';
import { useProjects } from '@/hooks/use-projects';
import { usePlatformModules } from '@/hooks/use-modules';
import { usePermissions } from '@/hooks/use-permissions';
import { PersonSearchComboBox, type PersonOption } from '@/components/shared/person-search-combo-box';
import { useOrgCurrency, useOrgToday } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import type { MileageStatus, Person } from '@/lib/types';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  reimbursed: 'bg-purple-100 text-purple-800',
};

export function MileagePage() {
  const orgCurrency = useOrgCurrency();
  const orgToday = useOrgToday();
  const permissions = usePermissions();
  const isManager = permissions.canManagePayroll;

  const [statusFilter, setStatusFilter] = useState<MileageStatus | ''>('');
  const [personFilter, setPersonFilter] = useState<PersonOption | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showForm, setShowForm] = useState(false);

  const { isModuleEnabled } = usePlatformModules();
  const { data: entries, isLoading } = useMileageEntries({
    status: statusFilter || undefined,
    ...(isManager
      ? {
          person: personFilter?.person_id ?? undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        }
      : {}),
  });
  const { data: settings } = usePayrollSettings();
  const { data: personsData } = usePersons({ page_size: 100 });
  const { data: projects } = useProjects({ page_size: 100 }, isModuleEnabled('projects'));

  const createMileage = useCreateMileage();
  const submitMileage = useSubmitMileage();
  const approveMileage = useApproveMileage();
  const rejectMileage = useRejectMileage();

  const [form, setForm] = useState({
    person: '',
    project: '',
    date: orgToday,
    description: '',
    miles: '',
    rate_per_mile: settings?.default_mileage_rate ?? '0.6700',
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.person || !form.description || !form.miles) return;
    await createMileage.mutateAsync({
      person: Number(form.person),
      project: form.project ? Number(form.project) : undefined,
      date: form.date,
      description: form.description,
      miles: form.miles,
      rate_per_mile: form.rate_per_mile,
    });
    setShowForm(false);
    setForm({
      person: '',
      project: '',
      date: orgToday,
      description: '',
      miles: '',
      rate_per_mile: settings?.default_mileage_rate ?? '0.6700',
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Mileage</h1>
          <p className="text-gray-600 mt-1">Track mileage for reimbursement</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)}>
          <Plus className="h-4 w-4 mr-2" />
          {showForm ? 'Cancel' : 'Log Mileage'}
        </Button>
      </div>

      {/* Quick Entry Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>New Mileage Entry</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label>Worker *</Label>
                  <Select value={form.person} onValueChange={(v) => setForm({ ...form, person: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select worker" />
                    </SelectTrigger>
                    <SelectContent>
                      {personsData?.results?.map((p: Person) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {p.first_name} {p.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Project</Label>
                  <Select value={form.project || 'none'} onValueChange={(v) => setForm({ ...form, project: v === 'none' ? '' : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {projects?.results?.map((proj) => (
                        <SelectItem key={proj.id} value={String(proj.id)}>
                          {proj.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                  <Label>Description *</Label>
                  <Input
                    placeholder="e.g., Drive to job site"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Miles *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={form.miles}
                    onChange={(e) => setForm({ ...form, miles: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Rate per Mile</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={form.rate_per_mile}
                    onChange={(e) => setForm({ ...form, rate_per_mile: e.target.value })}
                  />
                </div>
                <div className="flex items-end">
                  <div className="text-sm text-gray-500">
                    Total: {formatMoney(
                      form.miles && form.rate_per_mile
                        ? parseFloat(form.miles) * parseFloat(form.rate_per_mile)
                        : 0,
                      orgCurrency
                    )}
                  </div>
                </div>
              </div>
              <Button type="submit" disabled={createMileage.isPending}>
                {createMileage.isPending ? 'Creating...' : 'Create Entry'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v as MileageStatus | '')}>
          <SelectTrigger className="w-48" aria-label="Status filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="reimbursed">Reimbursed</SelectItem>
          </SelectContent>
        </Select>
        {isManager && (
          <>
            <PersonSearchComboBox
              value={personFilter}
              onChange={(option) => setPersonFilter(option?.person_id != null ? option : null)}
              placeholder="Search workers to filter..."
              endpoint="persons"
              className="w-64 mt-0"
            />
            <Input
              type="date"
              aria-label="From date"
              className="w-44"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <Input
              type="date"
              aria-label="To date"
              className="w-44"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </>
        )}
      </div>

      {/* Mileage List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading mileage entries...</div>
          ) : !(Array.isArray(entries) ? entries : []).length ? (
            <div className="p-8 text-center text-gray-500">
              <Car className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No mileage entries found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {(Array.isArray(entries) ? entries : []).map((entry) => (
                <div key={entry.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div>
                    <p className="font-medium text-gray-900">{entry.description}</p>
                    <p className="text-sm text-gray-500">
                      {entry.person_name ?? entry.tech_name ?? 'Unknown'} &middot; {entry.date}
                      {entry.project_name && ` &middot; ${entry.project_name}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-semibold">{entry.miles} mi</p>
                      <p className="text-sm text-gray-500">
                        {formatMoney(entry.total, orgCurrency)}
                      </p>
                    </div>
                    <Badge className={statusColors[entry.status] ?? 'bg-gray-100 text-gray-800'}>
                      {entry.status}
                    </Badge>
                    <div className="flex gap-1">
                      {entry.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => submitMileage.mutate(entry.id)}
                        >
                          Submit
                        </Button>
                      )}
                      {isManager && entry.status === 'submitted' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => approveMileage.mutate(entry.id)}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => rejectMileage.mutate(entry.id)}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
